import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit, Trash2, X, Save, Calendar, BookOpen, Package } from 'lucide-react';
import {
  getScopeAndSequenceByClass,
  createScopeAndSequence,
  updateScopeAndSequence,
  getClasses,
  getUnits,
  getLessons
} from '../firebase/lessonPlannerService';
import DashboardLayout from './DashboardLayout';
import './ScopeAndSequence.css';

function ScopeAndSequence() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { classId } = useParams();
  const [loading, setLoading] = useState(true);
  const [classInfo, setClassInfo] = useState(null);
  const [scopeAndSequence, setScopeAndSequence] = useState(null);
  const [units, setUnits] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(null);

  const [periodFormData, setPeriodFormData] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });

  const [entryFormData, setEntryFormData] = useState({
    entryType: 'unit', // 'unit', 'lesson', or 'manual'
    unitId: '',
    lessonId: '',
    topic: '',
    standards: [''],
    objectives: '',
    description: '',
    resources: ''
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, classId, navigate]);

  const loadData = async () => {
    setLoading(true);

    // Load class info
    const classesResult = await getClasses(currentUser.id);
    if (classesResult.success) {
      const foundClass = classesResult.classes.find(c => c.id === classId);
      setClassInfo(foundClass);
    }

    // Load units and lessons
    const [unitsResult, lessonsResult] = await Promise.all([
      getUnits(currentUser.id),
      getLessons(currentUser.id)
    ]);

    if (unitsResult.success) {
      // Filter units that belong to this class
      const classUnits = unitsResult.units.filter(unit =>
        unit.classIds && unit.classIds.includes(classId)
      );
      setUnits(classUnits);
    }

    if (lessonsResult.success) {
      // Filter lessons that belong to units in this class
      const unitIds = unitsResult.units
        .filter(unit => unit.classIds && unit.classIds.includes(classId))
        .map(unit => unit.id);

      const classLessons = lessonsResult.lessons.filter(lesson =>
        lesson.unitIds && lesson.unitIds.some(uid => unitIds.includes(uid))
      );
      setLessons(classLessons);
    }

    // Load scope and sequence
    const result = await getScopeAndSequenceByClass(classId);
    if (result.success) {
      setScopeAndSequence(result.scopeAndSequence);
      if (!result.scopeAndSequence) {
        // Create a new empty scope and sequence
        const newScope = {
          title: '',
          periodType: 'Quarter',
          periods: []
        };
        setScopeAndSequence(newScope);
        setIsEditing(true);
      }
    }

    setLoading(false);
  };

  const handleCreatePeriod = () => {
    if (!periodFormData.name || !periodFormData.startDate || !periodFormData.endDate) {
      alert('Please fill in period name, start date, and end date');
      return;
    }

    const newPeriod = {
      id: Date.now().toString(),
      name: periodFormData.name,
      startDate: periodFormData.startDate,
      endDate: periodFormData.endDate,
      entries: []
    };

    const updatedPeriods = [...(scopeAndSequence.periods || []), newPeriod];
    setScopeAndSequence({ ...scopeAndSequence, periods: updatedPeriods });

    // Clear the form
    setPeriodFormData({ name: '', startDate: '', endDate: '' });
  };

  const handleSave = async () => {
    if (!scopeAndSequence) return;

    if (scopeAndSequence.id) {
      // Update existing
      const result = await updateScopeAndSequence(scopeAndSequence.id, {
        title: scopeAndSequence.title || '',
        periodType: scopeAndSequence.periodType,
        periods: scopeAndSequence.periods || []
      });
      if (result.success) {
        setIsEditing(false);
        loadData();
      }
    } else {
      // Create new
      const result = await createScopeAndSequence(currentUser.id, classId, {
        title: scopeAndSequence.title || '',
        periodType: scopeAndSequence.periodType,
        periods: scopeAndSequence.periods || []
      });
      if (result.success) {
        setIsEditing(false);
        loadData();
      }
    }
  };

  const handleDeletePeriod = (index) => {
    if (window.confirm('Are you sure you want to delete this period?')) {
      const newPeriods = scopeAndSequence.periods.filter((_, i) => i !== index);
      setScopeAndSequence({ ...scopeAndSequence, periods: newPeriods });
    }
  };

  const handleUpdatePeriod = (index, field, value) => {
    const newPeriods = [...scopeAndSequence.periods];
    newPeriods[index] = { ...newPeriods[index], [field]: value };
    setScopeAndSequence({ ...scopeAndSequence, periods: newPeriods });
  };

  const handleAddEntry = (periodIndex) => {
    setSelectedPeriodIndex(periodIndex);
    setEntryFormData({
      entryType: 'unit',
      unitId: '',
      lessonId: '',
      topic: '',
      standards: [''],
      objectives: '',
      description: '',
      resources: ''
    });
    setEditingEntry(null);
    setShowEntryModal(true);
  };

  const handleEditEntry = (periodIndex, entryIndex) => {
    const entry = scopeAndSequence.periods[periodIndex].entries[entryIndex];
    setSelectedPeriodIndex(periodIndex);
    setEntryFormData({
      entryType: entry.entryType || 'manual',
      unitId: entry.unitId || '',
      lessonId: entry.lessonId || '',
      topic: entry.topic || '',
      standards: entry.standards || [''],
      objectives: entry.objectives || '',
      description: entry.description || '',
      resources: entry.resources || ''
    });
    setEditingEntry(entryIndex);
    setShowEntryModal(true);
  };

  const handleDeleteEntry = (periodIndex, entryIndex) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      const newPeriods = [...scopeAndSequence.periods];
      newPeriods[periodIndex].entries = newPeriods[periodIndex].entries.filter((_, i) => i !== entryIndex);
      setScopeAndSequence({ ...scopeAndSequence, periods: newPeriods });
    }
  };

  const handleEntrySubmit = (e) => {
    e.preventDefault();

    // Get the linked unit or lesson data if selected
    let linkedData = {};
    if (entryFormData.entryType === 'unit' && entryFormData.unitId) {
      const unit = units.find(u => u.id === entryFormData.unitId);
      if (unit) {
        linkedData = {
          topic: unit.title,
          description: unit.description || '',
          standards: unit.standards || []
        };
      }
    } else if (entryFormData.entryType === 'lesson' && entryFormData.lessonId) {
      const lesson = lessons.find(l => l.id === entryFormData.lessonId);
      if (lesson) {
        linkedData = {
          topic: lesson.title,
          objectives: lesson.learningObjective || '',
          description: lesson.description || '',
          standards: lesson.standards || []
        };
      }
    }

    const entryData = {
      id: editingEntry !== null
        ? scopeAndSequence.periods[selectedPeriodIndex].entries[editingEntry].id
        : Date.now().toString(),
      entryType: entryFormData.entryType,
      unitId: entryFormData.entryType === 'unit' ? entryFormData.unitId : null,
      lessonId: entryFormData.entryType === 'lesson' ? entryFormData.lessonId : null,
      topic: entryFormData.entryType === 'manual' ? entryFormData.topic : linkedData.topic,
      standards: entryFormData.entryType === 'manual'
        ? entryFormData.standards.filter(s => s.trim() !== '')
        : linkedData.standards || [],
      objectives: entryFormData.entryType === 'manual' ? entryFormData.objectives : linkedData.objectives || '',
      description: entryFormData.entryType === 'manual' ? entryFormData.description : linkedData.description || '',
      resources: entryFormData.resources
    };

    const newPeriods = [...scopeAndSequence.periods];
    if (editingEntry !== null) {
      newPeriods[selectedPeriodIndex].entries[editingEntry] = entryData;
    } else {
      if (!newPeriods[selectedPeriodIndex].entries) {
        newPeriods[selectedPeriodIndex].entries = [];
      }
      newPeriods[selectedPeriodIndex].entries.push(entryData);
    }

    setScopeAndSequence({ ...scopeAndSequence, periods: newPeriods });
    setShowEntryModal(false);
  };

  const getEntryIcon = (entry) => {
    if (entry.entryType === 'unit') return Package;
    if (entry.entryType === 'lesson') return BookOpen;
    return BookOpen;
  };

  const getEntryBadge = (entry) => {
    if (entry.entryType === 'unit') return 'Unit';
    if (entry.entryType === 'lesson') return 'Lesson';
    return null;
  };

  const handleAutoPopulate = () => {
    // Check if periods exist
    if (!scopeAndSequence?.periods || scopeAndSequence.periods.length === 0) {
      alert('Please create periods first before auto-populating. Click "Add Period" to set up your grading periods, semesters, or quarters with their dates.');
      return;
    }

    if (!lessons || lessons.length === 0) {
      alert('No lessons found for this class. Please create and schedule lessons first.');
      return;
    }

    // Filter lessons that have dates
    const scheduledLessons = lessons.filter(l => l.startDate || l.scheduledDate);

    if (scheduledLessons.length === 0) {
      alert('No scheduled lessons found. Please assign dates to your lessons first.');
      return;
    }

    // Auto-populate lessons into existing periods
    const updatedPeriods = scopeAndSequence.periods.map(period => {
      if (!period.startDate || !period.endDate) {
        return period; // Skip periods without dates
      }

      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);

      // Find lessons that fall within this period
      const periodLessons = scheduledLessons.filter(lesson => {
        const lessonDate = new Date(lesson.startDate || lesson.scheduledDate);
        return lessonDate >= periodStart && lessonDate <= periodEnd;
      });

      // Sort lessons by date
      periodLessons.sort((a, b) => {
        const dateA = new Date(a.startDate || a.scheduledDate);
        const dateB = new Date(b.startDate || b.scheduledDate);
        return dateA - dateB;
      });

      // Create entries from lessons
      const newEntries = periodLessons.map(lesson => ({
        id: Date.now().toString() + Math.random(),
        entryType: 'lesson',
        lessonId: lesson.id,
        topic: lesson.title,
        objectives: lesson.learningObjective || '',
        description: lesson.description || '',
        standards: lesson.standards || [],
        resources: ''
      }));

      return {
        ...period,
        entries: newEntries
      };
    });

    setScopeAndSequence({
      ...scopeAndSequence,
      periods: updatedPeriods
    });

    alert(`Successfully populated ${updatedPeriods.reduce((sum, p) => sum + (p.entries?.length || 0), 0)} lessons into ${updatedPeriods.length} periods!`);
  };

  const getUnitNamesForLesson = (lessonId) => {
    const lesson = lessons.find(l => l.id === lessonId);
    if (!lesson || !lesson.unitIds || lesson.unitIds.length === 0) {
      return '';
    }

    const lessonUnits = units.filter(u => lesson.unitIds.includes(u.id));
    return lessonUnits.map(u => u.unitNumber ? `Unit ${u.unitNumber}: ${u.title}` : u.title).join(', ');
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...entryFormData[field]];
    newArray[index] = value;
    setEntryFormData({ ...entryFormData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setEntryFormData({ ...entryFormData, [field]: [...entryFormData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = entryFormData[field].filter((_, i) => i !== index);
    setEntryFormData({ ...entryFormData, [field]: newArray });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading scope and sequence...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!classInfo) {
    return (
      <DashboardLayout>
        <div className="error-state">
          <p>Class not found</p>
          <button onClick={() => navigate('/classes')} className="btn-primary">
            Back to Classes
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="scope-sequence-container">
        <div className="page-header">
          <div>
            <button onClick={() => navigate('/classes')} className="back-button">
              ← Back to Classes
            </button>
            <h1>Scope & Sequence</h1>
            <p className="subtitle">{classInfo.name} - {classInfo.gradeLevel}</p>
          </div>
          <div className="header-actions">
            {isEditing ? (
              <>
                <button onClick={handleSave} className="btn-primary">
                  <Save size={20} />
                  Save Changes
                </button>
                <button onClick={() => setIsEditing(false)} className="btn-secondary">
                  Cancel
                </button>
              </>
            ) : (
              <button onClick={() => setIsEditing(true)} className="btn-primary">
                <Edit size={20} />
                Edit
              </button>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="scope-header-form">
            <div className="form-group">
              <label>Period Name</label>
              <input
                type="text"
                value={periodFormData.name}
                onChange={(e) => setPeriodFormData({ ...periodFormData, name: e.target.value })}
                placeholder="e.g., Quarter 1, Fall Semester"
              />
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={periodFormData.startDate}
                onChange={(e) => setPeriodFormData({ ...periodFormData, startDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={periodFormData.endDate}
                onChange={(e) => setPeriodFormData({ ...periodFormData, endDate: e.target.value })}
                min={periodFormData.startDate}
              />
            </div>
            <div className="form-group">
              <label>Period Type</label>
              <select
                value={scopeAndSequence?.periodType || 'Quarter'}
                onChange={(e) => setScopeAndSequence({ ...scopeAndSequence, periodType: e.target.value })}
              >
                <option value="Quarter">Quarter</option>
                <option value="Semester">Semester</option>
                <option value="Month">Month</option>
                <option value="Week">Week</option>
                <option value="Custom">Custom</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={handleCreatePeriod} className="btn-primary" type="button" style={{ width: '100%' }}>
                <Plus size={18} />
                Create Period
              </button>
            </div>
          </div>
        )}

        {scopeAndSequence?.title && !isEditing && (
          <div className="scope-title-display">
            <h2>{scopeAndSequence.title}</h2>
            <span className="period-type-badge">{scopeAndSequence.periodType}s</span>
          </div>
        )}

        <div className="scope-content">
          {isEditing && scopeAndSequence?.periods && scopeAndSequence.periods.length > 0 && (
            <div className="action-buttons">
              <button onClick={handleAutoPopulate} className="btn-secondary add-period-btn">
                <Calendar size={20} />
                Auto-Populate Lessons
              </button>
            </div>
          )}

          {isEditing && (!scopeAndSequence?.periods || scopeAndSequence.periods.length === 0) && (
            <div className="workflow-hint">
              <p><strong>Getting Started:</strong></p>
              <ol>
                <li>Fill in the period name, start date, and end date in the form above</li>
                <li>Click "Create Period" to add the period</li>
                <li>Repeat to add more periods</li>
                <li>Click "Save Changes" when done editing</li>
                <li>Use "Auto-Populate Lessons" to automatically fill periods with your scheduled lessons</li>
              </ol>
            </div>
          )}

          {scopeAndSequence?.periods && scopeAndSequence.periods.length > 0 ? (
            <div className="periods-grid">
              {scopeAndSequence.periods.map((period, periodIndex) => (
                <div key={period.id} className="period-card">
                  <div className="period-header">
                    <div className="period-title-section">
                      <Calendar className="period-icon" size={24} />
                      <div className="period-info-inline">
                        {isEditing ? (
                          <input
                            type="text"
                            value={period.name}
                            onChange={(e) => handleUpdatePeriod(periodIndex, 'name', e.target.value)}
                            className="period-name-input"
                            placeholder="Period Name"
                          />
                        ) : (
                          <h3>{period.name}</h3>
                        )}
                        {isEditing ? (
                          <div className="period-dates-edit">
                            <input
                              type="date"
                              value={period.startDate || ''}
                              onChange={(e) => handleUpdatePeriod(periodIndex, 'startDate', e.target.value)}
                              className="period-date-input"
                            />
                            <span>to</span>
                            <input
                              type="date"
                              value={period.endDate || ''}
                              onChange={(e) => handleUpdatePeriod(periodIndex, 'endDate', e.target.value)}
                              className="period-date-input"
                              min={period.startDate}
                            />
                          </div>
                        ) : (
                          period.startDate && period.endDate && (
                            <p className="period-dates">
                              {new Date(period.startDate).toLocaleDateString()} - {new Date(period.endDate).toLocaleDateString()}
                            </p>
                          )
                        )}
                      </div>
                    </div>
                    {isEditing && (
                      <div className="period-actions">
                        <button onClick={() => handleDeletePeriod(periodIndex)} className="btn-icon delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="period-entries">
                    {period.entries && period.entries.length > 0 ? (
                      period.entries.map((entry, entryIndex) => {
                        const EntryIcon = getEntryIcon(entry);
                        const badge = getEntryBadge(entry);
                        const unitNames = entry.entryType === 'lesson' && entry.lessonId ? getUnitNamesForLesson(entry.lessonId) : '';
                        return (
                        <div key={entry.id} className="entry-card">
                          <div className="entry-header">
                            <EntryIcon size={16} className="entry-icon" />
                            <div className="entry-title-container">
                              <h4>{entry.topic}</h4>
                              {badge && <span className="entry-type-badge">{badge}</span>}
                            </div>
                            {isEditing && (
                              <div className="entry-actions">
                                <button onClick={() => handleEditEntry(periodIndex, entryIndex)} className="btn-icon-small">
                                  <Edit size={14} />
                                </button>
                                <button onClick={() => handleDeleteEntry(periodIndex, entryIndex)} className="btn-icon-small delete">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                          {unitNames && (
                            <div className="entry-unit-info">
                              <Package size={14} className="unit-badge-icon" />
                              <span>{unitNames}</span>
                            </div>
                          )}
                          {entry.standards && entry.standards.length > 0 && (
                            <div className="entry-standards">
                              <strong>Standards:</strong> {entry.standards.join(', ')}
                            </div>
                          )}
                          {entry.objectives && (
                            <div className="entry-objectives">
                              <strong>Objectives:</strong> {entry.objectives}
                            </div>
                          )}
                          {entry.description && (
                            <p className="entry-description">{entry.description}</p>
                          )}
                          {entry.resources && (
                            <div className="entry-resources">
                              <strong>Resources:</strong> {entry.resources}
                            </div>
                          )}
                        </div>
                      );
                      })
                    ) : (
                      <p className="no-entries">No entries yet</p>
                    )}
                  </div>

                  {isEditing && (
                    <button onClick={() => handleAddEntry(periodIndex)} className="add-entry-btn">
                      <Plus size={16} />
                      Add Topic/Unit
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Calendar size={64} className="empty-icon" />
              <h3>No periods yet</h3>
              <p>Add {scopeAndSequence?.periodType?.toLowerCase()}s to organize your curriculum</p>
            </div>
          )}
        </div>

        {/* Entry Modal */}
        {showEntryModal && (
          <div className="modal-overlay" onClick={() => setShowEntryModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingEntry !== null ? 'Edit Entry' : 'Add to Scope & Sequence'}</h2>
              <form onSubmit={handleEntrySubmit}>
                <div className="form-group">
                  <label>Entry Type *</label>
                  <select
                    value={entryFormData.entryType}
                    onChange={(e) => setEntryFormData({ ...entryFormData, entryType: e.target.value })}
                  >
                    <option value="unit">Existing Unit</option>
                    <option value="lesson">Existing Lesson</option>
                    <option value="manual">Manual Entry</option>
                  </select>
                </div>

                {entryFormData.entryType === 'unit' && (
                  <div className="form-group">
                    <label>Select Unit *</label>
                    <select
                      value={entryFormData.unitId}
                      onChange={(e) => setEntryFormData({ ...entryFormData, unitId: e.target.value })}
                      required
                    >
                      <option value="">-- Select a Unit --</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>
                          {unit.unitNumber ? `Unit ${unit.unitNumber}: ` : ''}{unit.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {entryFormData.entryType === 'lesson' && (
                  <div className="form-group">
                    <label>Select Lesson *</label>
                    <select
                      value={entryFormData.lessonId}
                      onChange={(e) => setEntryFormData({ ...entryFormData, lessonId: e.target.value })}
                      required
                    >
                      <option value="">-- Select a Lesson --</option>
                      {lessons.map(lesson => (
                        <option key={lesson.id} value={lesson.id}>
                          {lesson.lessonNumber ? `Lesson ${lesson.lessonNumber}: ` : ''}{lesson.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {entryFormData.entryType === 'manual' && (
                  <>
                    <div className="form-group">
                      <label>Topic/Unit Name *</label>
                      <input
                        type="text"
                        value={entryFormData.topic}
                        onChange={(e) => setEntryFormData({ ...entryFormData, topic: e.target.value })}
                        required
                        placeholder="e.g., Linear Equations, Solving Inequalities"
                      />
                    </div>

                    <div className="form-group array-field">
                      <label>Standards</label>
                      {entryFormData.standards.map((standard, index) => (
                        <div key={index} className="array-input-row">
                          <input
                            type="text"
                            value={standard}
                            onChange={(e) => handleArrayFieldChange('standards', index, e.target.value)}
                            placeholder="e.g., 111.39.c.1.A"
                          />
                          {entryFormData.standards.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeArrayField('standards', index)}
                              className="btn-icon-small delete"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayField('standards')}
                        className="add-field-btn"
                      >
                        + Add Standard
                      </button>
                    </div>

                    <div className="form-group">
                      <label>Learning Objectives</label>
                      <textarea
                        value={entryFormData.objectives}
                        onChange={(e) => setEntryFormData({ ...entryFormData, objectives: e.target.value })}
                        placeholder="What will students learn?"
                        rows="3"
                      />
                    </div>

                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={entryFormData.description}
                        onChange={(e) => setEntryFormData({ ...entryFormData, description: e.target.value })}
                        placeholder="Brief description of the topic/unit"
                        rows="3"
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Additional Resources (optional)</label>
                  <textarea
                    value={entryFormData.resources}
                    onChange={(e) => setEntryFormData({ ...entryFormData, resources: e.target.value })}
                    placeholder="Materials, textbooks, online resources, etc."
                    rows="2"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowEntryModal(false)} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingEntry !== null ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

export default ScopeAndSequence;
